import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearch, useNavigate, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAgentSocket } from '@/hooks/use-agent-socket';
import { SessionSidebar } from '@/components/chat/session-sidebar';
import { MessageList } from '@/components/chat/message-list';
import { MessageInput } from '@/components/chat/message-input';
import { EventLog } from '@/components/chat/event-log';
import { ConnectionStatus } from '@/components/chat/connection-status';
import type { RuntimeEvent, AgentSession } from '@/lib/agent-types';

// ── Top Bar ─────────────────────────────────────────────────────

function TopBar({
  sessionId,
  isConnected,
  showEventLog,
  onToggleEventLog,
}: {
  sessionId: string | null;
  isConnected: boolean;
  showEventLog: boolean;
  onToggleEventLog: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b px-4 py-2">
      <div className="flex items-center gap-4">
        <Link to="/" className="text-muted-foreground hover:text-foreground text-sm">
          &larr; Home
        </Link>
        <ConnectionStatus isConnected={isConnected} sessionId={sessionId} />
        {sessionId && (
          <span className="text-muted-foreground truncate font-mono text-xs">{sessionId}</span>
        )}
      </div>
      <button
        type="button"
        onClick={onToggleEventLog}
        className="text-muted-foreground hover:text-foreground text-xs"
      >
        {showEventLog ? 'Hide Events' : 'Show Events'}
      </button>
    </div>
  );
}

// ── Event state wrapper ─────────────────────────────────────────

function useLocalEvents(sourceEvents: RuntimeEvent[]) {
  const [events, setEvents] = useState<RuntimeEvent[]>([]);
  const [clearedAt, setClearedAt] = useState(0);

  useEffect(() => {
    setEvents(sourceEvents.slice(clearedAt));
  }, [sourceEvents, clearedAt]);

  const clearEvents = useCallback(() => {
    setClearedAt((prev) => prev + events.length);
    setEvents([]);
  }, [events.length]);

  return { events, clearEvents };
}

// ── Error Banner ────────────────────────────────────────────────

function ErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;

  return (
    <div className="bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
      {error}
    </div>
  );
}

// ── Chat Page ───────────────────────────────────────────────────

export function ChatPage() {
  const { session: sessionId } = useSearch({ from: '/chat' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEventLog, setShowEventLog] = useState(true);

  // Check if the selected session is completed from cached data
  const sessionsData = queryClient.getQueryData<{ sessions: AgentSession[] }>(['sessions']);
  const activeSession = useMemo(
    () => sessionsData?.sessions.find((s) => s.id === sessionId),
    [sessionsData, sessionId]
  );
  const isCompleted = activeSession?.status === 'completed';

  // Don't connect WebSocket for completed sessions
  const socket = useAgentSocket(isCompleted ? null : (sessionId ?? null));
  const { events, clearEvents } = useLocalEvents(socket.events);

  const handleSelectSession = useCallback(
    (id: string) => {
      navigate({ to: '/chat', search: { session: id } });
    },
    [navigate]
  );

  const handleDeleteSession = useCallback(
    (id: string) => {
      if (id === sessionId) {
        navigate({ to: '/chat', search: {} });
      }
    },
    [navigate, sessionId]
  );

  const toggleEventLog = useCallback(() => {
    setShowEventLog((prev) => !prev);
  }, []);

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <div className="w-[280px] shrink-0 border-r">
        <SessionSidebar
          activeSessionId={sessionId ?? null}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
        />
      </div>

      {/* Center + Right area */}
      <div className="flex min-w-0 flex-1">
        {/* Center: chat area */}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar
            sessionId={sessionId ?? null}
            isConnected={isCompleted ? false : socket.isConnected}
            showEventLog={showEventLog}
            onToggleEventLog={toggleEventLog}
          />
          {isCompleted ? (
            <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
              This session has ended.
            </div>
          ) : (
            <>
              <ErrorBanner error={socket.error} />
              <MessageList messages={socket.messages} />
              <MessageInput onSend={socket.send} disabled={!socket.isConnected} />
            </>
          )}
        </div>

        {/* Right: event log (collapsible) */}
        {showEventLog && (
          <div className="w-[320px] shrink-0 border-l">
            <EventLog events={events} onClear={clearEvents} />
          </div>
        )}
      </div>
    </div>
  );
}
